import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useQuery } from "@tanstack/react-query";

interface CheckrEmbedOptions {
  sessionTokenPath: string;
  sessionTokenRequestHeaders: () => Record<string, string>;
  fakeMode?: boolean;
  env?: string;
}

interface CheckrDisclosureOptions extends CheckrEmbedOptions {
  accountName: string;
  candidateFullName: string;
  language: "en";
  candidateId: string;
  packageSlug: string;
  workLocationState: string;
  styles?: Record<string, unknown>;
  onLoadDisclosureError: (maybeError: unknown) => void;
  onConsentSubmitError: (maybeError: unknown) => void;
  onConsentSubmitSuccess: () => void;
}

interface CheckrDisclosureEmbed {
  render: (selector: string) => void;
}

interface CheckrSdk {
  Embeds: {
    DisclosureConsent: new (options: CheckrDisclosureOptions) => CheckrDisclosureEmbed;
  };
}

declare global {
  interface Window {
    Checkr?: CheckrSdk;
  }
}

interface UseCheckrScriptResult {
  isLoading: boolean;
  isError: boolean;
  Checkr: CheckrSdk | undefined;
}

// We're using Checkr from CDN since their React SDK supports only React 18, and they have no plans to support React 19+ right now
// https://docs.checkr.com/embeds/#section/Overview
export const CHECKR_SDK_URL = "https://cdn.jsdelivr.net/npm/@checkr/web-sdk/dist/web-sdk.umd.js";
const SCRIPT_ID = "checkr-web-sdk";

/**
 * Loads the Checkr Web SDK script dynamically
 * @param sdkUrl - The URL of the Checkr SDK
 * @param signal - AbortSignal for cleanup
 * @returns Promise that resolves with the Checkr SDK object
 */
async function loadCheckrScript(sdkUrl: string, signal?: AbortSignal): Promise<CheckrSdk> {
  // Return immediately if already loaded
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line unicorn/prefer-global-this
  if (window.Checkr) {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line unicorn/prefer-global-this
    return window.Checkr;
  }

  // Create and inject script
  // Note: React Query handles deduplication if multiple components call this simultaneously
  return await new Promise<CheckrSdk>((resolve, reject) => {
    const script = document.createElement("script");

    function handleLoad(): void {
      cleanup();
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line unicorn/prefer-global-this
      if (window.Checkr) {
        logEvent(APP_V2_APP_EVENTS.CHECKR_SDK_LOADED);
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line unicorn/prefer-global-this
        resolve(window.Checkr);
      } else {
        reject(new Error("Checkr SDK loaded but not available on window"));
      }
    }

    function handleError(this: HTMLScriptElement, event: Event): void {
      const error = new Error("Failed to load Checkr SDK script");
      logError(APP_V2_APP_EVENTS.CHECKR_SDK_LOAD_ERROR, {
        error,
        metadata: {
          sdkUrl,
          event,
        },
      });
      cleanup();
      script.remove();
      reject(error);
    }

    function cleanup(): void {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
    }

    try {
      script.id = SCRIPT_ID;
      script.src = sdkUrl;
      script.async = true;
      script.addEventListener("load", handleLoad, { once: true, signal });
      script.addEventListener("error", handleError, { once: true, signal });
      document.head.append(script);
    } catch (error) {
      logError(APP_V2_APP_EVENTS.CHECKR_SDK_LOAD_ERROR, { error });
      cleanup();
      script.remove();
      reject(error);
    }
  });
}

/**
 * Hook to dynamically load the Checkr Web SDK from CDN
 * Uses React Query for automatic request deduplication, caching, and error handling
 * @returns Loading state, error state, and the Checkr SDK object
 */
export function useCheckrScript(): UseCheckrScriptResult {
  const { checkrSdkUrl } = useCbhFlag(CbhFeatureFlag.RUN_BACKGROUND_CHECK_ON_BOOKING, {
    defaultValue: {
      isEnabled: false, // does not matter. If any component calls this hook, the feature should be enabled.
      checkrSdkUrl: CHECKR_SDK_URL,
      isDelayedBackgroundCheckNoticeEnabled: false,
    },
  });

  const result = useQuery<CheckrSdk>({
    queryKey: ["checkr-sdk", checkrSdkUrl],
    queryFn: async ({ signal }): Promise<CheckrSdk> => await loadCheckrScript(checkrSdkUrl, signal),
    staleTime: Number.POSITIVE_INFINITY, // Script never becomes stale once loaded
    cacheTime: Number.POSITIVE_INFINITY, // Keep in cache forever
    retry: 3, // Retry failed loads up to 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000), // Exponential backoff
  });

  return {
    isLoading: result.isLoading,
    isError: result.isError,
    Checkr: result.data,
  };
}
